import React, { useState, forwardRef, useCallback, useEffect } from 'react'
import { Select, Stack, Box, Text, Label } from '@sanity/ui'
import { set, unset } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

interface VehicleValue {
  make?: {
    _ref: string
    _type: 'reference'
  }
  model?: {
    _ref: string
    _type: 'reference'
  }
  generation?: {
    _ref: string
    _type: 'reference'
  }
  submodel?: {
    _ref: string
    _type: 'reference'
  }
}

interface MakeOption {
  _id: string
  name: string
  models?: ModelOption[]
}

interface ModelOption {
  _id: string
  name: string
  generations?: GenerationOption[]
}

interface GenerationOption {
  _id: string
  name: string
  submodels?: SubmodelOption[]
}

interface SubmodelOption {
  _id: string
  name: string
}

const VehicleInput = forwardRef<HTMLDivElement, ObjectInputProps>((props, ref) => {
  const [makeOptions, setMakeOptions] = useState<MakeOption[]>([])
  const [inputValues, setInputValues] = useState({
    make: '',
    model: '',
    generation: '',
    submodel: '',
  })

  const { value, onChange, schemaType } = props
  const client = useClient()

  const handleChange = useCallback(
    (newValue: VehicleValue | undefined) => {
      onChange(newValue ? set(newValue) : unset())
    },
    [onChange]
  )

  const handleMakeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.make?._ref === inputValue) {
        return
      }

      setInputValues({
        make: inputValue,
        model: '',
        generation: '',
        submodel: '',
      })

      handleChange({
        make: {
          _ref: inputValue,
          _type: 'reference',
        },
        model: undefined,
        generation: undefined,
        submodel: undefined,
      })
    },
    [handleChange, value]
  )

  const handleModelChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.model?._ref === inputValue) {
        return
      }

      setInputValues({
        make: inputValues.make,
        model: inputValue,
        generation: '',
        submodel: '',
      })

      handleChange({
        make: value?.make,
        model: {
          _ref: inputValue,
          _type: 'reference',
        },
        generation: undefined,
        submodel: undefined,
      })
    },
    [handleChange, value, inputValues.make]
  )

  const handleGenerationChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.generation?._ref === inputValue) {
        return
      }

      setInputValues({
        make: inputValues.make,
        model: inputValues.model,
        generation: inputValue,
        submodel: '',
      })

      handleChange({
        make: value?.make,
        model: value?.model,
        generation: {
          _ref: inputValue,
          _type: 'reference',
        },
        submodel: undefined,
      })
    },
    [handleChange, value, inputValues.make, inputValues.model]
  )

  const handleSubmodelChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.submodel?._ref === inputValue) {
        return
      }

      setInputValues({
        make: inputValues.make,
        model: inputValues.model,
        generation: inputValues.generation,
        submodel: inputValue,
      })

      handleChange({
        make: value?.make,
        model: value?.model,
        generation: value?.generation,
        submodel: {
          _ref: inputValue,
          _type: 'reference',
        },
      })
    },
    [handleChange, value, inputValues.make, inputValues.model, inputValues.generation]
  )

  // Fetch vehicle hierarchy data
  useEffect(() => {
    client
      .fetch(
        `*[_type == "make" && !(_id in path("drafts.**"))]{
          _id,
          name,
          models[!@->.isHidden]->{
            _id,
            name,
            generations[]->{
              _id,
              name,
              submodels[]->{
                _id,
                name,
                hasSubmodels => {
                  submodels[]->{
                    _id,
                    name
                  }
                }
              }
            }
          }
        }`
      )
      .then((data: MakeOption[]) => {
        let currentMakeOption: MakeOption | undefined
        let currentModelOption: ModelOption | undefined
        let currentGenerationOption: GenerationOption | undefined
        let currentSubmodelOption: SubmodelOption | undefined
        
        if (value?.make?._ref) {
          currentMakeOption = data.find((make) => make._id === value.make!._ref)

          if (currentMakeOption && value?.model?._ref) {
            currentModelOption = currentMakeOption.models?.find(
              (model) => model._id === value.model!._ref
            )

            if (currentModelOption && value?.generation?._ref) {
              currentGenerationOption = currentModelOption.generations?.find(
                (gen) => gen._id === value.generation!._ref
              )

              if (currentGenerationOption && value?.submodel?._ref) {
                currentSubmodelOption = currentGenerationOption.submodels?.find(
                  (submodel) => submodel._id === value.submodel!._ref
                )
              }
            }
          }
        }

        setInputValues({
          make: currentMakeOption?._id || '',
          model: currentModelOption?._id || '',
          generation: currentGenerationOption?._id || '',
          submodel: currentSubmodelOption?._id || '',
        })

        const sorted = data.sort((a, b) => {
          return a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        })
        setMakeOptions(sorted)
      })
      .catch((error) => {
        console.error('Error fetching vehicle data:', error)
      })
  }, [client, value])

  const modelOptions =
    !value?.make || makeOptions.length === 0
      ? []
      : makeOptions.find((make) => make._id === value.make._ref)?.models || []

  const generationOptions =
    !value?.model || modelOptions.length === 0
      ? []
      : modelOptions.find((model) => model._id === value.model._ref)?.generations || []

  const submodelOptions =
    !value?.generation || generationOptions.length === 0
      ? []
      : generationOptions.find((gen) => gen._id === value.generation._ref)?.submodels || []

  return (
    <Stack gap={3} ref={ref}>
      <Box
        paddingX={3}
        style={{ borderLeft: '1px solid var(--card-border-color)' }}
      >
        <Stack gap={3}>
          <Box>
            <Label size={1} weight="medium">Make</Label>
            <Select
              value={inputValues.make}
              onChange={handleMakeChange}
            >
              <option disabled value="">Select Make</option>
              {makeOptions.map((make) => (
                <option key={make._id} value={make._id}>
                  {make.name}
                </option>
              ))}
            </Select>
          </Box>
          
          {modelOptions?.length > 0 && (
            <Box>
              <Label size={1} weight="medium">Model</Label>
              <Select
                value={inputValues.model}
                onChange={handleModelChange}
              >
                <option disabled value="">Select Model</option>
                {modelOptions.map((model) => (
                  <option key={model._id} value={model._id}>
                    {model.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}
          
          {generationOptions?.length > 0 && (
            <Box>
              <Label size={1} weight="medium">Generation</Label>
              <Select
                value={inputValues.generation}
                onChange={handleGenerationChange}
              >
                <option disabled value="">Select Generation</option>
                {generationOptions.map((generation) => (
                  <option key={generation._id} value={generation._id}>
                    {generation.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}
          
          {submodelOptions?.length > 0 && (
            <Box>
              <Label size={1} weight="medium">Submodel</Label>
              <Select
                value={inputValues.submodel}
                onChange={handleSubmodelChange}
              >
                <option disabled value="">Select Submodel</option>
                {submodelOptions.map((submodel) => (
                  <option key={submodel._id} value={submodel._id}>
                    {submodel.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  )
})

VehicleInput.displayName = 'VehicleInput'

export default VehicleInput